/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import moment from 'moment';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { AggregationInterval } from '../../../shared/entities/dataUsage';
import { selectCumulativeUsageByNetwork } from '../../../store';
import { requestCumulativeUsageByNetwork } from '../../../store/dataUsage/actions';
import { PlaceholderWrapper, PlaceholderHeading, PlaceholderText } from '../DataUsageChartsCommonComponents';
import DataUsageChartContainer from './DataUsageChartContainer';
import UsageLineChart from './UsageLineChart';

function translateChartData(data: { [sourceTime: number]: number }) {
  return Object.keys(data)
    .sort()
    .map(timestamp => ({
      x: moment.unix(parseInt(timestamp)).toDate(),
      y: data[timestamp],
    }));
}

function getSeriesData(data: { [sourceTime: number]: number } | undefined, name: string, shortenedName: string) {
  return {
    name,
    data: data ? translateChartData(data) : [],
    tooltipShortenedName: shortenedName,
  };
}
export interface DailyNetworkUsageChartProps {
  width: number;
}
function CumulativeNetworkUsageChart({ width }: DailyNetworkUsageChartProps) {
  const dispatch = useDispatch();
  const cumulativeUsageByNetwork = useSelector(selectCumulativeUsageByNetwork, shallowEqual);

  const series = [
    getSeriesData(cumulativeUsageByNetwork?.kaCumulativeUsageBytes, 'High Capacity Ka', 'High Capacity Ka'),
    getSeriesData(cumulativeUsageByNetwork?.kuCumulativeUsageBytes, 'VGNet', 'VGNet'),
  ];

  return (
    <DataUsageChartContainer
      width={width}
      header="Network Usage (Cumulative)"
      aggregationInterval={cumulativeUsageByNetwork?.aggregationInterval}
      menuItemClickHandler={(aggregationInterval: AggregationInterval) => {
        if (cumulativeUsageByNetwork) {
          dispatch(
            requestCumulativeUsageByNetwork({
              fromMsSinceEpoch: cumulativeUsageByNetwork.fromMsSinceEpoch,
              toMsSinceEpoch: cumulativeUsageByNetwork.toMsSinceEpoch,
              datapathUuids: cumulativeUsageByNetwork.datapathUuids,
              aggregationInterval: aggregationInterval,
            }),
          );
        }
      }}
    >
      <UsageLineChart
        series={series}
        placeholder={
          <PlaceholderWrapper>
            <PlaceholderHeading>No Terminals</PlaceholderHeading>
            <PlaceholderText>To view data usage analytics, select one or multiple terminals</PlaceholderText>
          </PlaceholderWrapper>
        }
        showPlaceholder={cumulativeUsageByNetwork === undefined}
        aggregationInterval={cumulativeUsageByNetwork?.aggregationInterval}
        userEndDateMs={cumulativeUsageByNetwork?.toMsSinceEpoch}
        width={width}
      />
    </DataUsageChartContainer>
  );
}

export default CumulativeNetworkUsageChart;
